import { defineStore } from "pinia"
import { ref } from "vue"
import type { Opportunity } from "@/model/DataModel"
import { UseRootStore } from "@/model/RootStore"
import type { View } from "@/types"
import * as apiClient from "@/utils/ApiClient"

type FetchByViewOptions = {
	limit?: number
	offset?: number
	projectId?: string | null
}

type ProjectOpportunitiesInvalidation = {
	projectId: string
	revision: number
	selectOpportunityId: number | null
}

type InvalidateProjectOpportunitiesOptions = {
	selectOpportunityId?: number | null
}

export const useOpportunityStore = defineStore("opportunity", () => {
	const rootStore = UseRootStore()
	const projectOpportunitiesInvalidation = ref<ProjectOpportunitiesInvalidation | null>(null)
	let invalidationRevision = 0

	async function fetchByView(
		view: View,
		options: FetchByViewOptions = {}
	): Promise<{ items: Opportunity[]; total: number }> {
		return await apiClient.getOpportunitiesByView(
			rootStore.userApiToken,
			view,
			options.projectId,
			options.limit,
			options.offset
		)
	}

	async function fetchBrokerNameSourcesByView(
		view: View,
		projectId?: string | null
	): Promise<apiClient.OpportunityBrokerNameSource[]> {
		return await apiClient.getOpportunityBrokerNameSourcesByView(rootStore.userApiToken, view, projectId)
	}

	function invalidateProjectOpportunities(
		projectId: string,
		options: InvalidateProjectOpportunitiesOptions = {}
	): void {
		projectOpportunitiesInvalidation.value = {
			projectId,
			revision: ++invalidationRevision,
			selectOpportunityId: options.selectOpportunityId ?? null,
		}
	}

	return {
		fetchByView,
		fetchBrokerNameSourcesByView,
		projectOpportunitiesInvalidation,
		invalidateProjectOpportunities,
	}
})
